<template>
    <div id="Clinician__PastDue" class="dashboard-card d-flex">
        <CardTemplate borderClass="past-due-section">
            <!-- HEADER -->
            <template v-slot:header>
                <section class="card-title">
                    <span> Past Due </span>
                    <span class="ms-1" v-if="tasksList.length"> ({{ tasksList.length }}) </span>
                    <a
                        tabindex="0"
                        href="javascript:void(0);"
                        role="button"
                        v-custom-popover="{ content: tooltip, className: 'w-25 font-size-sm info-popup' }"
                    >
                        <i class="fas fa-info-circle text-primary ms-2"></i>
                    </a>
                </section>
                <section class="ms-auto d-flex align-items-center cursor-pointer">
                    <div
                        v-show="tasksList.length"
                        v-tooltip="{ title: `Click to sort visits by schedule date.` }"
                        @click="sortByScheduleDate()"
                        class="d-flex flex-column align-items-center"
                    >
                        <span class="icon small">
                            <i :class="isAscending ? 'fas fa-long-arrow-alt-down' : 'fas fa-long-arrow-alt-up'"></i>
                            <i class="fas fa-calendar-alt"></i>
                        </span>
                        <span class="text font-size-sm fw-bold me-2 blue-text">Sort</span>
                    </div>
                </section>
            </template>
            <!-- CONTENT -->
            <!-- LOADING -->
            <template v-slot:content>
                <div class="mt-3 text-center" v-if="isLoading && !hasError">
                    <i class="fas fa-spinner fa-pulse fa-fw"></i>
                    <b>Loading your Past Due Visits</b>
                    <i class="fas fa-spinner fa-pulse fa-fw"></i>
                </div>
                <!-- ERROR -->
                <div class="mt-5 text-center" v-if="hasError && !isLoading">
                    <p>
                        There was a problem while fetching your past due tasks.
                        <br />
                        Please try again.
                    </p>
                    <button class="btn btn-primary" @click="getPastDueTasks()">Try Again</button>
                </div>
                <!-- NO DATA -->
                <section v-if="tasksList.length === 0 && !isLoading && !hasError" class="no-data">
                    <i class="mb-3"> Hooray! You don't have any visits that are past due. </i>
                </section>
                <!-- DATA -->
                <div
                    v-if="tasksList.length && !isLoading && !hasError"
                    class="d-flex flex-column h-100 overflow-auto"
                    :class="`${userOs == 'Windows' ? 'custom-scrollbar' : ''}`"
                >
                    <div class="past-due" v-for="task in tasksList" :key="task.Id">
                        <section class="task-info py-2">
                            <div class="d-flex align-items-center justify-content-between font-size-xs date-container">
                                <div class="left-section">
                                    <span>{{ formatDate(task.Date) }}</span>
                                    <span class="font-size-md mx-2 grey-secondary-text" v-html="bulletSymbol"></span>
                                    <a
                                        href="javascript:void(0);"
                                        @click="viewPatient(task)"
                                        class="me-1 mb-1 font-size-xs"
                                    >
                                        {{ task.patientFullName }}
                                    </a>
                                </div>
                                <div class="right-section">
                                    <span class="px-2 strip-item dropdown app-menu">
                                        <a
                                            class="text-dark fw-bold font-size-sm"
                                            href="javascript:void(0);"
                                            id="User_Dropdown"
                                            role="button"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                        >
                                            <i class="fas fa-ellipsis-h ellipsis"></i>
                                        </a>
                                        <div
                                            class="dropdown-menu dropdown-menu-end mt-0 font-size-xs text-center"
                                            aria-labelledby="User_Dropdown"
                                        >
                                            <a
                                                href="javascript:void(0);"
                                                @click="openMissedVisitDialog(task)"
                                                v-if="task.Application == applicationEnum.HomeCare"
                                                :disabled="!isCompleteMissedVisit"
                                                :class="`${!isCompleteMissedVisit ? 'disabled' : ''}`"
                                            >
                                                <span
                                                    :class="`${
                                                        !isCompleteMissedVisit ? 'disabled' : 'text-danger'
                                                    } text-center`"
                                                    >Start Missed Visit</span
                                                >
                                            </a>
                                            <a v-else href="javascript:void(0);" @click="openMissedVisitDialog(task)">
                                                <span class="text-danger text-center">Start Missed Visit</span>
                                            </a>
                                            <li>
                                                <a
                                                    v-if="task.statusNameType"
                                                    href="javascript:void(0);"
                                                    v-tooltip="{ title: task.buttonTooltip }"
                                                    @click="startTask(task)"
                                                >
                                                    <span class="text-center blue-text">{{ task.statusNameType }}</span>
                                                </a>
                                            </li>
                                        </div>
                                    </span>
                                </div>
                            </div>
                            <div class="fw-bold d-flex flex-column">
                                <div class="d-flex align-items-center justify-content-between">
                                    <span class="w-60"> {{ task.VisitName }} </span>
                                </div>
                            </div>

                            <div class="d-flex justify-content-between align-items-center flex-lg-row">
                                <div class="d-flex align-items-center">
                                    <ProductPill
                                        class="application-name"
                                        :product-id="getApplicationId(task.Application)"
                                    />
                                    <span
                                        class="text-default ms-2 font-size-2xs"
                                        v-tooltip="{ title: task.agencyTooltip }"
                                    >
                                        {{ task.BranchName }}
                                    </span>
                                </div>
                                <div>
                                    <i
                                        class="ms-2 fas fa-sticky-note blue-note"
                                        v-tooltip="{ title: task.EpisodeNotes, className: `blue-note` }"
                                        v-if="task.EpisodeNotes"
                                    ></i>
                                    <i
                                        class="ms-2 fas fa-sticky-note yellow-note"
                                        v-tooltip="{ title: task.Comments, className: `yellow-note` }"
                                        v-if="task.Comments"
                                    ></i>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </template>
        </CardTemplate>
    </div>
</template>

<script lang="ts" src="./PastDue.ts"></script>
<style src="./PastDue.scss"></style>
